import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    updateDoc,
    addDoc,
    serverTimestamp,
    onSnapshot,
    orderBy,
    deleteDoc,
    setDoc,
    deleteField,
    getDoc,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import ReceptionDashboard from './ReceptionDashboard';
import HousekeepingDashboard from './HousekeepingDashboard';
import InventoryDashboard from './InventoryDashboard';
import SettingsDashboard from './SettingsDashboard'; 
import Sidebar from './Sidebar';
import Chat from './Chat';
import DashboardAccessGuard from "./DashboardAccessGuard";

// Helper initial state & utilities
const DashboardMetric = ({ title, value, color }) => (
    <div style={{ textAlign: 'center', padding: '10px 15px', borderLeft: `5px solid ${color}`, minWidth: '150px' }}>
        <p style={{ margin: 0, fontSize: '1.5em', fontWeight: 'bold', color: color }}>{value}</p>
        <small style={{ margin: 0, color: '#555' }}>{title}</small>
    </div>
);

const calculateTotalDays = (checkIn, checkOut) => {
    const oneDay = 24 * 60 * 60 * 1000;
    const first = new Date(checkIn);
    const second = new Date(checkOut);
    if (isNaN(first) || isNaN(second) || second <= first) return 0;
    return Math.ceil((new Date(second).getTime() - new Date(first).getTime()) / oneDay);
};
const initialRoomState = { 
    roomNumber: '', 
    price: '', 
    type: 'Standard',
    status: 'available', 
};

const generateAccessKey = () =>
    Math.random().toString(36).substring(2, 8).toUpperCase();

// OFFLINE INVOICE DOWNLOAD FUNCTION
const generateAndDownloadInvoice = async (guestData, hotelName) => {
    const docPdf = new jsPDF();
    const invoiceId = `${guestData.hotelId}_${guestData.id}_${Date.now()}`;
    const pricePerNight = guestData.roomPrice || guestData.price || 0;
    const checkInDate = guestData.checkInDate?.toDate ?
        guestData.checkInDate.toDate() : new Date(guestData.checkInDate);
    const checkOutDate = guestData.checkOutDate?.toDate ? guestData.checkOutDate.toDate() : new Date(guestData.checkOutDate);
    const totalNights = Math.max(1, Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)));
    const totalAmount = guestData.adjustedFinalPrice ||
        (pricePerNight * totalNights).toFixed(2); 

    docPdf.setFont('helvetica');
    docPdf.setFontSize(22);
    docPdf.setTextColor(40, 40, 40);
    docPdf.text(`INVOICE: ${hotelName}`, 10, 20);
    docPdf.setFontSize(10);
    docPdf.setTextColor(100, 100, 100);
    docPdf.text(`Invoice ID: ${invoiceId}`, 150, 20);
    docPdf.setFontSize(14);
    docPdf.setTextColor(40, 40, 40);
    docPdf.text('Billed To:', 10, 40);
    docPdf.setFontSize(12);
    docPdf.text(`${guestData.fullName}`, 10, 46);
    docPdf.text(`Email: ${guestData.email}`, 10, 52);

    let y = 70;
    docPdf.setFontSize(10);
    docPdf.setFillColor(230, 230, 230);
    docPdf.rect(10, y, 190, 8, 'F');
    docPdf.text('Description', 15, y + 5);
    docPdf.text('Price/Night', 80, y + 5);
    docPdf.text('Total Nights', 120, y + 5);
    docPdf.text('Final Charge', 170, y + 5);
    y += 8;
    
    docPdf.setFontSize(12);
    docPdf.text(`Room ${guestData.roomNumber} (${guestData.roomType || 'Standard'})`, 15, y + 5);
    docPdf.text(`$${pricePerNight.toFixed(2)}`, 80, y + 5);
    docPdf.text(`${totalNights}`, 120, y + 5);
    docPdf.text(`$${totalAmount}`, 170, y + 5); 
    
    y += 15;
    docPdf.line(10, y, 200, y);
    
    docPdf.setFontSize(16);
    docPdf.setFont('helvetica', 'bold');
    docPdf.text('TOTAL CHARGE:', 130, y + 10);
    docPdf.text(`$${totalAmount}`, 170, y + 10);
    
    docPdf.setFontSize(10);
    docPdf.setFont('helvetica', 'normal');
    docPdf.text(`Check-in: ${checkInDate.toLocaleDateString()}`, 10, y + 30);
    docPdf.text(`Check-out: ${checkOutDate.toLocaleDateString()}`, 60, y + 30);

    const filename = `Invoice_${guestData.fullName.replace(/\s/g, '_')}_Room${guestData.roomNumber}.pdf`;
    docPdf.save(filename);
};


// Main Component

export default function MainDashboard({ dashboardKey }) {
    const { t } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    // === NEW STATE FOR NAVIGATION ===
    const [activeDashboard, setActiveDashboard] = useState('welcome');
    // ================================

    const [guests, setGuests] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    
    
    const [snackbar, setSnackbar] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [stayModificationRequests, setStayModificationRequests] = useState([]);
    const [activeChats, setActiveChats] = useState([]);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [isMaximized, setIsMaximized] = useState(false); 
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [lostItems, setLostItems] = useState([]);
    const [guestReviews, setGuestReviews] = useState([]);
    const [newLostItem, setNewLostItem] = useState({ 
        name: '', 
        roomNumber: '',
        photoUrl: '', // CRITICAL FIX: Ensure this property exists
        status: 'pending',
        description: '',
        foundInRoom: '',
        dateFound: new Date().toISOString().substring(0, 10), 
        condition: 'Good',
        
    });
    const [inventoryItems, setInventoryItems] = useState([]);
    const [newInventoryItem, setNewInventoryItem] = useState({
        name: '', 
        currentStock: 0,
        lowStockThreshold: 10,
        category: 'General', 
    });
    const [hotelSettings, setHotelSettings] = useState({ 
        wifiSSID: '', 
        wifiPassword: '', 
        housekeepingMessage: '5 minutes'
    });
    const [pendingRegistrationCount, setPendingRegistrationCount] = useState(0);
    const [pendingGuestRequests, setPendingGuestRequests] = useState([]);
    const [checkoutRequests, setCheckoutRequests] = useState([]);
    const [openBillGuestId, setOpenBillGuestId] = useState(null);
const [currentBillDetails, setCurrentBillDetails] = useState({
    // Form inputs for a new item
    description: '',
    amount: '',
    // The actual bill details from the guest object
    items: [], 
    total: 0 
});    
    // ⭐ FIX: Add state for room management inputs
    const [newRoom, setNewRoom] = useState({ 
        roomNumber: '', 
        roomType: 'Standard',
        type: 'Standard', 
        price: 100, 
        maxCapacity: 2 
    });
    const getDisplayDate = (dateField) => {
    if (!dateField) return 'N/A';
    // Handles both Firestore Timestamp objects and standard Date objects
    const date = typeof dateField.toDate === 'function' ? dateField.toDate() : dateField;
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fetchRooms = async () => {
    // We already have access to hotelId, setRooms, and db because they are defined 
    // in the outer scope of the MainDashboard component.

    // Check if the ID is available before querying the database
    if (!hotelId) {
        console.log("Waiting for hotelId to load...");
        return; 
    }
    
    try {
        const roomsRef = collection(db, "rooms");
        
        // Use the defined hotelId variable (Line 53 error is here)
        const q = query(roomsRef, where("hotelId", "==", hotelId)); 
        const roomSnapshot = await getDocs(q);
        
        // Define roomsList using 'const'
        const roomsList = roomSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Use the defined setRooms variable (Line 56 error is here)
        setRooms(roomsList); 
        
    } catch (error) {
        console.error("Error fetching rooms:", error);
    }
};   
    const hotelId = user?.hotelId;
    const hotelName = user?.hotelName;

    // Helper functions
    const formatStatus = useCallback((status) => 
        status ? status.replace(/_/g, ' ').toUpperCase() : 'N/A', 
        []
    );
    // Status color helper - moved here for use in renderDashboard and RoomManagement
    // eslint-disable-next-line no-unused-vars
    const getStatusColor = (status) => {
        switch (status) {
            case 'available': return 'var(--color-success)';
            case 'Needs Cleaning': return 'var(--color-error)';
            case 'Cleaning In Progress': return 'orange';
            case 'Cleaning En Route': return 'blue';
            case 'Under Maintenance': return 'gray';
            case 'Inspecting': return '#8a2be2'; 
            case 'Out of Service': return '#8b0000';
            case 'occupied': return 'var(--color-primary)';
            case 'DO_NOT_DISTURB': return '#ff4500';
            default: return 'var(--color-secondary)';
        }
    };
   

    // --------------------------------------------------------------------------------
    // FIREBASE LISTENERS (useEffect)
    // --------------------------------------------------------------------------------
    // === Fetch Guests Requesting Checkout ===
useEffect(() => {
  const checkout = guests.filter(g => g.status === 'CHECKOUT_REQUESTED');
  setCheckoutRequests(checkout);
}, [guests]);

    // 1. Fetch Guests, Rooms
  useEffect(() => {
    if (!hotelId) return;

    // --- 1. GUEST REGISTRATIONS LISTENER (The one for the COUNT) ---
    // This query is CORRECTLY set up to ONLY fetch documents with the PENDING status.
    const unsubscribeRegistrations = onSnapshot(
        query(
            collection(db, 'guestRegistrations'), 
            where('hotelId', '==', hotelId),
            // ✅ FIX 1: Correctly place 'where' as a separate argument in the query function.
            where('status', '==', 'PENDING_DIGITAL_CHECKIN'), 
            orderBy('submittedAt', 'desc') // ✅ Correct orderBy syntax
        ),
        (snapshot) => {
            // FIX 2: Since the query is already filtered, the count is simply the number of documents returned.
            // You must use a dedicated state setter for the count here.
            setPendingRegistrationCount(snapshot.docs.length); 
            // Note: If you have a separate state for the list of pending registrations, you would set it here too.
        },
        (err) => {
            console.error(t('errors.fetch_registrations'), err);
            // No need to set error/loading for a metric listener, but keep console.error.
        }
    );

    // --- 2. GUEST LISTENER (For your main Guests/Active Stays list) ---
    // Note: The 'guests' list must come from the 'guests' collection, not 'guestRegistrations'.
    const unsubscribeGuests = onSnapshot(
        query(collection(db, 'guests'), where('hotelId', '==', hotelId), orderBy('checkInDate', 'desc')),
        (snapshot) => {
            const guestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGuests(guestsData); // Set your main guest list
            
            // ❌ REMOVED: All counting logic is removed from here because the registrations listener handles it.
            
            setLoading(false);
        },
        (err) => {
            console.error(t('errors.fetch_guests'), err);
            setError(t('errors.load_fail'));
            setLoading(false);
        }
    );
    
    // --- 3. ROOMS LISTENER (Syntax Fixed) ---
    const unsubscribeRooms = onSnapshot(
        query(
            collection(db, 'rooms'), 
            where('hotelId', '==', hotelId), 
            orderBy('roomNumber', 'asc') 
        ),
        (snapshot) => {
            const roomsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRooms(roomsData);
        },
        (err) => {
            console.error(t('errors.fetch_rooms'), err);
            setError("Failed to load room data.");
        }
    );
    
    // --- Cleanup ---
    return () => {
        unsubscribeRegistrations(); // New cleanup line
        unsubscribeGuests();
        unsubscribeRooms();
    };
}, [hotelId]);
    // 3. Fetch Guest Requests (In-Stay requests like "Towels", "Water")
    useEffect(() => {
        // This relies on the guests state being updated by the first listener
        const pending = guests
            .filter(g => g.hasPendingRequest)
            .map(g => ({
                id: g.id,
         
                fullName: g.fullName,
                roomNumber: g.roomNumber,
                requestType: t(`requests.${g.requestType.toLowerCase()}`, { defaultValue: g.requestType }),
                requestDetails: g.requestDetails,
                requestTimestamp: g.requestTimestamp,
                requestTimeDisplay: g.requestTimestamp?.toDate().toLocaleTimeString() || 'N/A'
     
            }));
        setPendingGuestRequests(pending);
        
    }, [guests]);
    // 4. Fetch Active Chats
    useEffect(() => {
        if (!hotelId || !user?.uid) return;
        const q = query(
            collection(db, 'chats'),
            where('hotelId', '==', hotelId),
            orderBy('lastMessageTimestamp', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
     
            const chats = snapshot.docs.map(doc => {
                const chatData = doc.data();
                const staffLastViewedTime = chatData.staffLastViewed?.[user.uid]?.toDate()?.getTime() || 0;
                const lastMessageTime = chatData.lastMessageTimestamp?.toDate()?.getTime() || 0;
                const isUnread = chatData.guestHasUnread || 
      
                           (chatData.lastSenderId !== user.uid && lastMessageTime > staffLastViewedTime);

                return {
                    id: doc.id,
                    ...chatData,
          
                    isUnread: isUnread,
                    lastMessageTime: chatData.lastMessageTimestamp?.toDate(),
                };
            });
            setActiveChats(chats);
            setUnreadChatCount(chats.filter(chat => chat.isUnread).length);
        }, (err) => {
            console.error(t('errors.load_chats'), err);
            setError(t('errors.load_chats'));
        });

        return unsubscribe;
    }, [hotelId, user?.uid]);
    
    // 5. Fetch Lost & Found Items
    useEffect(() => {
        if (!hotelId) return;
        // NOTE: Index required for hotelId + timestamp
        const q = query(
            collection(db, 'lost_and_found'),
            where('hotelId', '==', hotelId),
            orderBy('timestamp', 'desc')
      
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                dateFoundDisplay: doc.data().dateFound || 'N/A' 
            
            }));
            setLostItems(items);
        }, (err) => {
            console.error("Lost & Found listener failed:", err);
        });

        return unsubscribe;
    }, [hotelId]);
    // 6. Fetch Inventory Stock
    useEffect(() => {
        if (!hotelId) return;
        const q = query(
            collection(db, 'inventory_stock'),
            where('hotelId', '==', hotelId),
            orderBy('name', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
       
            const items = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                currentStock: Number(doc.data().currentStock), 
                lowStockThreshold: Number(doc.data().lowStockThreshold) 
            }));
       
            setInventoryItems(items);
        }, (err) => {
            console.error("Inventory Stock listener failed:", err);
        });

        return unsubscribe;
    }, [hotelId]);
    // 7. Fetch Hotel Settings
    useEffect(() => {
        if (!hotelId) return;

        const settingsRef = doc(db, 'hotel_settings', hotelId);

        const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                setHotelSettings(prev => ({ ...prev, ...docSnap.data() }));
            } else {
    
                console.log("No hotel settings found, using defaults.");
            }
        }, (err) => {
            console.error("Settings listener failed:", err);
        });

        return unsubscribe;
    }, [hotelId]);
    // Listener for Stay Modification Requests
useEffect(() => {
        // Assume hotelId is available from useAuth or another context
        if (!hotelId) {
    console.warn("Listener skipped: Hotel ID is not available on initial load.");
    
    return; // Exit the useEffect function until hotelId is ready
}
        // Query the 'requests' collection, filtering by hotelId, request type, and 'Pending' status.
        const stayRequestQuery = query(
    collection(db, 'requests'),
    where("hotelId", "==", hotelId), // 🔑 CRITICAL: Security & Scoping
    where('type', 'in', ['earlyCheckIn', 'lateCheckOut']), // 🔑 CRITICAL: Relevance
    where("isPending", "==", true), 
    orderBy('createdAt', 'desc')
);
        console.log(`Setting up Stay Modification Request listener for hotel: ${hotelId}`);

        const unsubscribe = onSnapshot(stayRequestQuery, (snapshot) => {
            const newRequests = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate().toLocaleTimeString() || 'N/A' // Format timestamp
            }));
            
           snapshot.docChanges().forEach(change => {
    if (change.type === "added" && !snapshot.metadata.fromCache) {
        const newRequest = change.doc.data();
        
        const readableType = newRequest.type 
            ? newRequest.type.replace(/([A-Z])/g, ' $1').toLowerCase() 
            : t('common.na');

        const alertMessage = t('alerts.new_stay_mod', { 
            room: newRequest.roomNumber, 
            type: readableType 
        });

        // 1. Show the notification
        setSuccess(alertMessage); 

        // 2. Clear it automatically after 5 seconds
        setTimeout(() => {
            setSuccess('');
        }, 5000);
    }
});

            setStayModificationRequests(newRequests);
            console.log(`Fetched ${newRequests.length} pending stay modification requests.`);
        }, (error) => {
            console.error("Error fetching stay modification requests:", error);
            // You might want to set a local error state here
        });

        // Cleanup function
        return () => unsubscribe();
    }, [hotelId]);; // Dependency array: runs when hotelId changes
    // 8. Fetch Guest Reviews
   // Listener for Guest Reviews
useEffect(() => {
    if (!hotelId) return;

    const reviewsQuery = query(
        collection(db, 'reviews'),
        where('hotelId', '==', hotelId), // Filters by hotel
        orderBy('timestamp', 'desc') // Sorts newest first
    );

    const unsubscribe = onSnapshot(reviewsQuery, (snapshot) => {
        const reviews = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Format timestamp for display
            timestampDisplay: doc.data().timestamp?.toDate().toLocaleString() || 'N/A'
        }));
        setGuestReviews(reviews);
    }, (error) => {
        console.error("Error fetching real-time reviews:", error);
        // Assuming you have an setError state, or just log the error
    });

    return () => unsubscribe();
}, [hotelId]);
useEffect(() => {
        // Only run if hotelId is actually present
        if (hotelId) { 
            fetchRooms();
        }
    }, [hotelId]);
useEffect(() => {
  if (!user?.hotelId) return;

  const hotelRef = doc(db, "users", user.hotelId);
  getDoc(hotelRef).then((snap) => {
    if (snap.exists()) {
      setHotelSettings(snap.data());
    }
  });
}, [user]);
    // --------------------------------------------------------------------------------
    // CORE HANDLERS 
    // --------------------------------------------------------------------------------
    // Inside your MainDashboard/HotelDashboard component function:

const handleMarkReviewAsRead = useCallback(async (reviewId) => {
    setLoading(true); setError(''); setSuccess('');

    try {
        const reviewRef = doc(db, 'guestReviews', reviewId);
        await updateDoc(reviewRef, {
            isReviewed: true,
            reviewedBy: user.uid,
            reviewedAt: serverTimestamp()
        });
        setSuccess(t('success.review_marked_read', { id: reviewId }));
    } catch (err) {
        console.error("Error marking review as read:", err);
        setError(t('errors.review_update_failed', { msg: err.message }));
    } finally {
        setLoading(false);
    }
}, [user?.uid]);
    // 1. Pre-Booking Handlers
    const handleAcceptPreBooking = useCallback(async (
        guestDocId, 
        guestData, 
        finalRoomId, 
        finalRoomNumber, 
        finalPricePerNight, 
        finalInvoiceUrl = "N/A"
    ) => {
        if (!guestDocId || 
            !finalRoomId) {
            console.error("Missing critical data for check-in acceptance.");
            return;
        }

        try {
            const checkIn = guestData.arrivalTime || guestData.checkInDate;
            const checkOut = guestData.departureTime || guestData.checkOutDate;
            const totalDays = calculateTotalDays(checkIn, checkOut);
   
            const totalPrice = totalDays * finalPricePerNight;
            const accessKey = generateAccessKey();

            // 2. Update Room Status (Mark as occupied and link guest)
            await updateDoc(doc(db, "rooms", finalRoomId), {
                status: "occupied",
                
                currentGuestId: guestDocId, 
            });
            // 3. Update Guest Document (Finalize Check-in)
            await updateDoc(doc(db, 'guests', guestDocId), {
                status: 'CHECKED_IN', 
                roomNumber: finalRoomNumber,
                roomId: finalRoomId,
                roomPrice: finalPricePerNight,
         
                totalDays: totalDays,
                totalPrice: totalPrice, 
                accessKey: accessKey, 
                invoiceUrl: finalInvoiceUrl,
                checkInTimestamp: serverTimestamp(), // Use serverTimestamp for accuracy
                checkedInBy: 
                user?.email || "Staff",
            });
            setSuccess(t('success.prebooking_accepted', { name: guestData.fullName, room: finalRoomNumber }));
        } catch (error) {
            console.error("Error finalizing pre-booking check-in:", error);
            setError(t('errors.prebooking_accept_failed'));
        }
    }, [user]);
    const handleRejectPreBooking = useCallback(async (guestDocId, reason = "Booking could not be confirmed. Please contact the front desk.") => {
        if (!guestDocId) return;
        try {
            await updateDoc(doc(db, 'guests', guestDocId), {
                status: 'REJECTED',
                rejectionReason: reason,
             
                checkedInBy: user?.email || "Staff",
            });
            setSuccess(t('success.prebooking_rejected', { id: guestDocId }));
        } catch (error) {
            console.error("Error rejecting pre-booking:", error); 
            setError(t('errors.prebooking_reject_failed'));
        }
    }, [user]);
    const toggleBillForm = useCallback((guestId) => {
    setOpenBillGuestId(prevId => prevId === guestId ? null : guestId);
    // You might want to fetch or initialize the current bill details here
    setCurrentBillDetails({ items: [], total: 0 }); 
}, []);

// Placeholder/Stub handlers for bill editing (required by ReceptionDashboard)
const handleBillChange = useCallback((e) => {
    const { name, value } = e.target;
    setCurrentBillDetails(prev => ({
        ...prev,
        [name]: value
    }));
}, []);
const handleAddItemToBill = useCallback(async (guest) => {
    if (!currentBillDetails.description || !currentBillDetails.amount) {
        setSnackbar({ type: 'error', message: 'Please enter a description and amount.' });
        return;
    }
    
    setLoading(true);

    const newItem = {
        description: currentBillDetails.description,
        amount: parseFloat(currentBillDetails.amount),
        timestamp: new Date(), // Correctly uses imported function
        staffId: user.uid,
    };

    try {
        const guestRef = doc(db, 'guests', guest.id); // Correctly uses imported function
        
        // Ensure addOnsDetails is an array, then add the new item
        const updatedAddOnsDetails = [...(guest.addOnsDetails || []), newItem];
        
        // Calculate the new total for all add-ons
        const newTotalAddOns = updatedAddOnsDetails.reduce((sum, item) => sum + item.amount, 0);

        await updateDoc(guestRef, { // Correctly uses imported function
            addOnsDetails: updatedAddOnsDetails,
            addOnsPrice: newTotalAddOns,
        });
        
        setSnackbar({ type: 'success', message: `Item added to Room ${guest.roomNumber}'s bill successfully.` });

        // Reset the form fields in the state
        setCurrentBillDetails(prev => ({
            ...prev,
            description: '',
            amount: '',
        }));

    } catch (error) {
        console.error('Error adding item to bill:', error);
        setSnackbar({ type: 'error', message: 'Failed to add bill item. Check console.' });
    } finally {
        setLoading(false);
    }
    // CRITICAL: Ensure all external dependencies are here
}, [currentBillDetails.description, currentBillDetails.amount, user.uid, db]);
const handleRemoveBillItem = useCallback(async (guest, itemIndex) => {
    if (!window.confirm('Are you sure you want to remove this bill item?')) return;
    
    setLoading(true);
    
    try {
        const guestRef = doc(db, 'guests', guest.id); // Correctly uses imported function
        
        // Create a new array excluding the item at itemIndex
        const updatedAddOnsDetails = (guest.addOnsDetails || []).filter((_, index) => index !== itemIndex);
        
        // Recalculate the new total
        const newTotalAddOns = updatedAddOnsDetails.reduce((sum, item) => sum + item.amount, 0);

        await updateDoc(guestRef, { // Correctly uses imported function
            addOnsDetails: updatedAddOnsDetails,
            addOnsPrice: newTotalAddOns,
        });

        setSnackbar({ type: 'success', message: 'Bill item removed successfully.' });
    } catch (error) {
        console.error('Error removing item from bill:', error);
        setSnackbar({ type: 'error', message: 'Failed to remove bill item. Check console.' });
    } finally {
        setLoading(false);
    }
    // CRITICAL: Ensure all external dependencies are here
}, [db]);
    // 2. Checkout Handler
    const handleFinalizeCheckout = useCallback(async (guestId, roomId) => {
        const guest = guests.find(g => g.id === guestId);
        if (!guest) return alert(t('errors.guest_not_found'));

        if (!window.confirm(t('common.confirm_checkout', { room: guest.roomNumber }))) return;
        
        setLoading(true);
        setError(''); setSuccess('');
  
       
        try {
            const guestForInvoice = {
                ...guest,
                checkInDate: guest.checkInDate?.toDate ? guest.checkInDate.toDate() : new Date(guest.checkInDate),
                checkOutDate: guest.checkOutDate?.toDate ? guest.checkOutDate.toDate() : new Date(guest.checkOutDate),
          
            };
            generateAndDownloadInvoice(guestForInvoice, hotelName);

            // 2. Update Guest status to intermediate state
            await updateDoc(doc(db, 'guests', guestId), {
                status: 'AWAITING_GUEST_FEEDBACK', 
                checkOutStaffId: user.uid, 
              
                checkOutDate: serverTimestamp(), 
                checkOutRequest: deleteField(), // Clear the request flag
            });
            // 3. Update Room status 
            await updateDoc(doc(db, 'rooms', roomId), {
                status: 'Needs Cleaning',
                available: false,
                currentGuestId: null,
            });
            // 4. Send Notification to the Guest
            const notificationMessage = t('notifications.checkout_complete_msg', { room: guest.roomNumber });
            await addDoc(collection(db, "notifications"), {
                hotelId: guest.hotelId, 
                guestId: guest.id,
                message: notificationMessage,
                timestamp: serverTimestamp(),
                isRead: false,
           
                type: 'CHECKOUT_COMPLETE',
            });
            setSuccess(t('success.checkout_finalized', { name: guest.fullName, room: guest.roomNumber }));
        } catch (err) {
            console.error("Error finalizing checkout:", err);
            setError(t('errors.checkout_failed'));
        } finally {
            setLoading(false);
        }
    }, [guests, hotelName, user?.uid]);

    // 3. Housekeeping Notification Handler 
    const handleNotifyGuest = useCallback(async (guest) => {
        setError(''); setSuccess(''); setLoading(true);
        
        const messageETA = hotelSettings.housekeepingMessage || '5 minutes';
        if (!window.confirm(t('common.confirm_notify_housekeeping', { time: messageETA, name: guest.fullName, room: guest.roomNumber }))) {
        setLoading(false);
            return;
  
        }
        
        try {
            const notificationText = t('notifications.housekeeping_eta_msg', { room: guest.roomNumber, time: messageETA });
            let chatId;
    
            // Retrieve existing chat or create a new 
            const chatQuery = query(
                collection(db, "chats"),
                where("guestId", "==", guest.id),
                where("hotelId", "==", guest.hotelId)
            );
            const chatSnap = await getDocs(chatQuery);
            if (chatSnap.empty) {
                const newChatRef = doc(collection(db, "chats"));
                await setDoc(newChatRef, {
                    hotelId: guest.hotelId,
                    guestId: guest.id,
                    guestName: guest.fullName,
                    roomNumber: guest.roomNumber,
              
                    lastMessageText: notificationText,
                    lastMessageTimestamp: serverTimestamp(),
                    lastSenderId: user.uid,
                    guestHasUnread: true,
                    staffLastViewed: { [user.uid]: serverTimestamp() }
      
                });
                chatId = newChatRef.id;
            } else {
                chatId = chatSnap.docs[0].id;
                await updateDoc(doc(db, 'chats', chatId), {
                    lastMessageText: notificationText,
                    lastMessageTimestamp: serverTimestamp(),
                    lastSenderId: user.uid,
                    guestHasUnread: true,
            
                    staffLastViewed: { [user.uid]: serverTimestamp() }
                });
            }

            // Add the notification message to the chat's messages subcollection
            await addDoc(collection(db, 'chats', chatId, 'messages'), {
                senderId: user.uid,
                senderRole: 'staff',
                text: notificationText,
           
                timestamp: serverTimestamp(),
                read: false
            });
            // Update Room status
            await updateDoc(doc(db, 'rooms', guest.roomId), {
                status: 'Cleaning En Route',
            });
            setSuccess(t('success.housekeeping_notified', { room: guest.roomNumber }));
        } catch (err) {
            console.error("Error sending notification:", err);
            setError(t('errors.notification_failed'));
        } finally {
            setLoading(false);
        }
    }, [hotelSettings, user?.uid]);

    // 4. Update Room Status (Used in Room Management Dropdown/Housekeeping)
    const updateRoomStatus = useCallback(async (roomId, newStatus) => {
        setLoading(true); setError(''); setSuccess('');
        try {
            const roomRef = doc(db, 'rooms', roomId);
            const isAvailable = newStatus === 'available';
            
        
            // If setting to available, ensure guest link is removed
            let updateData = {
                status: newStatus,
                available: isAvailable, 
            };
            if (isAvailable) {
           
                updateData.currentGuestId = null;
            }
            
            await updateDoc(roomRef, updateData);
            setSuccess(t('success.room_status_updated', { status: formatStatus(newStatus) }));
        } catch (error) {
            console.error("Failed to update room status:", error); 
          
            setError(t('errors.room_status_update_failed'));
        } finally {
            setLoading(false);
        }
    }, [formatStatus]);
    
    // 5. Resolve Guest Request (In-stay, e.g., 'Towels')
    const handleResolveGuestRequest = useCallback(async (guestId, requestSummary) => {
        setLoading(true); setError(''); setSuccess('');
        try {
            // 1. Update the guest document to clear request fields
            await updateDoc(doc(db, 'guests', guestId), {
                hasPendingRequest: false,
   
                requestTimestamp: deleteField(), 
                requestType: deleteField(),
                requestDetails: deleteField(),
                lastRequestResolvedTime: serverTimestamp(),
            });
            
          
            // 2. Add explicit notification for the guest app
            const guestRef = doc(db, 'guests', guestId);
            const guestSnap = await getDoc(guestRef);
            const guestInQuestion = guestSnap.data();
            const roomNumber = guestInQuestion?.roomNumber || 'N/A';
            const notificationMessage = t('notifications.request_resolved_msg', {
            request: requestSummary,
            room: roomNumber
        });
            await addDoc(collection(db, "notifications"), {
                hotelId: hotelId,
                guestId: guestId,
                message: notificationMessage,
                timestamp: serverTimestamp(),
                isread: false,
      
                type: 'REQUEST_RESOLVED',
            });
            // 3. OPTIMISTIC UI FIX: Manually update the local 'guests' state
            setGuests(prevGuests => prevGuests.map(g => g.id === guestId ? { 
                ...g, 
                hasPendingRequest: false, 
                requestTimestamp: null, 
                
                requestType: null, 
                requestDetails: null 
            } : g ) );
            setSuccess(t('success.request_resolved', { room: roomNumber }));
        } catch (err) {
            console.error("Failed to resolve guest request:", err);
            setError(t('errors.resolve_request_failed'));
        } finally {
            setLoading(false);
        }
    }, [hotelId]);

    const handleResolveStayRequest = useCallback(async (requestId) => {
    // Note: The ReceptionDashboard only passes requestId for simplicity.
    if (!window.confirm(t('common.confirm_approval'))) return;
    setLoading(true); setError(''); setSuccess('');

    try {
        const requestRef = doc(db, 'requests', requestId);
        const requestSnap = await getDoc(requestRef);
        
        if (!requestSnap.exists()) {
            console.error("Stay modification request not found:", requestId);
            setError(t('errors.request_not_found'));
            return;
        }
        
        const requestData = requestSnap.data();
        const guestId = requestData.guestId;
        const roomNumber = requestData.roomNumber; 

        // 1. Update the request status (This removes it from the list)
        await updateDoc(requestRef, {
            isPending: false,
            status: 'Approved', // Or 'Denied' if you implement a separate deny button
            resolvedAt: serverTimestamp(),
            resolvedBy: user.uid
        });

        // 2. CRITICAL: Update the Guest's actual booking/reservation with the new checkout date
        // Assuming you have a 'bookings' collection and the request contains the booking ID/key
        if (requestData.bookingKey && requestData.newCheckoutDate) {
            const bookingRef = doc(db, 'bookings', requestData.bookingKey);
            await updateDoc(bookingRef, {
                checkOutDate: requestData.newCheckoutDate
            });
        }

        // 3. Send Notification to the Guest
        if (guestId) {
            const notificationMessage = 
                t('notifications.stay_mod_approved', {
                room: roomNumber,
                date: new Date(requestData.newCheckoutDate).toLocaleDateString()
            });

            await addDoc(collection(db, "notifications"), {
                hotelId: hotelId, 
                guestId: guestId,
                message: notificationMessage,
                timestamp: serverTimestamp(),
                isread: false, 
                type: 'STAY_MOD_RESOLVED',
            });
            setSuccess(t('success.stay_mod_resolved'));
        } else {
            setSuccess(`Request ${requestId} resolved.`);
        }
        
    } catch (err) {
        console.error("Error resolving stay request:", err);
        setError(t('errors.generic_fail', { msg: err.message }));
    } finally {
        setLoading(false);
    }
}, [hotelId, user?.uid]);
    // 7. Lost & Found Handlers
    const handleRegisterLostItem = useCallback(async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!newLostItem.description || !newLostItem.dateFound) {
            return setError(t('errors.lost_item_fields'));
        }
        setLoading(true);
        try {
            await addDoc(collection(db, 'lost_and_found'), {
                ...newLostItem,
                hotelId: hotelId,
                status: 'Pending Retrieval', 
                timestamp: serverTimestamp(), 
     
            });
            setNewLostItem(prev => ({ 
                ...prev, 
                description: '', 
                foundInRoom: '', 
                condition: 'Good', 
                photoUrl: '', 
            
            }));
            setSuccess(t('success.lost_item_registered', { item: newLostItem.description }));
        } catch (error) {
            console.error("Error registering lost item:", error);
            setError(t('errors.lost_item_failed'));
        } finally {
            setLoading(false);
        }
    });

    const handleUpdateLostItemStatus = useCallback(async (itemId, newStatus) => {
        setLoading(true); setError(''); setSuccess('');
        try {
            await updateDoc(doc(db, 'lost_and_found', itemId), {
                status: newStatus,
                resolvedAt: newStatus === 'Pending Retrieval' ? deleteField() : serverTimestamp(),
            
            });
            setSuccess(`Lost item updated to: ${newStatus}`);
        } catch (error) {
            console.error("Failed to update lost item status:", error);
            setError("Failed to update status.");
        } finally {
            setLoading(false);
        }
    }, []);
    // 8. Inventory Handlers
    const handleAddNewInventoryItem = useCallback(async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        const trimmedName = newInventoryItem.name.trim();
        if (!trimmedName || newInventoryItem.currentStock < 0 || newInventoryItem.lowStockThreshold < 0) {
            return setError(t('errors.inventory_fields'));
        }
        const exists = inventoryItems.some(item => item.name.toLowerCase() === trimmedName.toLowerCase());
        if (exists) {
            return setError(t('errors.inventory_exists', { name: trimmedName }));
        }
        setLoading(true);
        try {
            await addDoc(collection(db, 'inventory_stock'), {
                name: trimmedName,
                currentStock: Number(newInventoryItem.currentStock),
                lowStockThreshold: Number(newInventoryItem.lowStockThreshold),
                hotelId: hotelId,
       
                timestamp: serverTimestamp(),
            });
            setNewInventoryItem({ name: '', currentStock: 0, lowStockThreshold: 10 });
            setSuccess(t('success.inventory_added', { name: trimmedName }));
        } catch (error) {
            console.error("Error adding inventory item:", error);
            setError(t('errors.inventory_failed'));
        } finally {
            setLoading(false);
        }
    });

    const handleUpdateInventory = useCallback(async (itemId, field, value) => {
        setLoading(true); setError(''); setSuccess('');
        try {
            const updateData = { [field]: Number(value) };
            await updateDoc(doc(db, 'inventory_stock', itemId), updateData);
            setSuccess(`Inventory updated successfully.`);
        } catch (error) {
         
            console.error("Error updating inventory:", error);
            setError("Failed to update inventory.");
        } finally {
            setLoading(false);
        }
    }, []);
    // 9. Settings Handler
    const handleSaveSettings = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true); setError(''); setSuccess('');
        try {
            await setDoc(doc(db, 'hotel_settings', hotelId), hotelSettings, { merge: true });
            setSuccess(t('success.settings_saved'));
        } catch (error) {
            console.error("Error saving settings:", error);
            setError(t('errors.settings_save_failed'));
        } finally {
            setLoading(false);
        }
    });
    
    // 10. Chat Handlers (Multi-Staff Aware)
    const handleSelectChat = useCallback(async (chatId) => {
        setSelectedChatId(chatId);
        setIsMaximized(false); 
        if (!user?.uid) return;
        
        // 1. OPTIMISTIC UI UPDATE: Mark as read
        setActiveChats(prevChats => prevChats.map(chat => 
            chat.id === chatId ? { ...chat, 
                isUnread: false } : chat
        ));

        // 2. FIREBASE UPDATE: Update the staffLastViewed map
        try {
            await updateDoc(doc(db, 'chats', chatId), {
                [`staffLastViewed.${user.uid}`]: serverTimestamp(),
            });
        } catch (e) {
        
            console.error("Error updating last viewed timestamp:", e);
        }
    }, [user?.uid]);
    const handleCloseChat = useCallback(() => {
        setSelectedChatId(null);
        setIsMaximized(false);
    }, []);
    const handleMaximizeToggle = useCallback(() => {
        setIsMaximized(prev => !prev);
    }, []);
    const handleDeleteChat = useCallback(async (chatId, guestName, roomNumber) => {
   const confirmMessage = t('common.confirm_delete_chat', { 
        name: guestName, 
        room: roomNumber 
    });
    
    if (!window.confirm(confirmMessage)) return;
        setLoading(true); setError(''); setSuccess('');
        try {
            await deleteDoc(doc(db, 'chats', chatId));
            if (selectedChatId === chatId) {
              
                setSelectedChatId(null);
            }
            setSuccess(t('success.chat_deleted', { name: guestName }));
        } catch (error) {
            console.error("Failed to delete chat:", error);
            setError(t('errors.chat_delete_failed'));
        } finally {
            setLoading(false);
    
        }
    }, [selectedChatId]);

    // ⭐ FIX: Add basic room management handlers (stubs) for props used in ReceptionDashboard
    const handleRoomChange = (e) => {
    const { name, value } = e.target;
    setError(''); setSuccess(''); // Clear messages on interaction

    setNewRoom(prev => ({
        ...prev,
        [name]: value 
    }));
};


// 2. FORM SUBMISSION HANDLER (Your original, working Firestore logic, plus a fix)
const handleAddRoom = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!newRoom.roomNumber || !newRoom.price || !newRoom.type) { // Added !newRoom.type check
         return setError(t('errors.room_fields_required'));
    }

    setLoading(true);
    try {
        // Check for existing room number within the hotel
        const roomQuery = query(
            collection(db, "rooms"),
            where("hotelId", "==", hotelId),
            where("roomNumber", "==", newRoom.roomNumber)
        );
        const roomSnap = await getDocs(roomQuery);
        if (!roomSnap.empty) {
            return setError(t('errors.room_exists', { number: newRoom.roomNumber }));
        }

        // Add the new document
        const roomRef = await addDoc(collection(db, 'rooms'), {
            ...newRoom,
            hotelId: hotelId,
            price: parseFloat(newRoom.price), // Ensure price is stored as a number
            // available: newRoom.status === 'available', // This can be simplified or managed by status
        });

        // CRITICAL FIX: Save the document ID back to the document itself for consistency
        await updateDoc(roomRef, { id: roomRef.id });

        // ** CRITICAL FIX FOR DISPLAY: Reload the room list **
        // Assuming you have a function called fetchRooms() that updates the 'rooms' state.
        if (typeof fetchRooms === 'function') {
            await fetchRooms(hotelId, setRooms); // Fetches and updates the list from Firestore
        } else {
            // FALLBACK (If fetchRooms is not available, manually update the local state)
            setRooms(prevRooms => [...prevRooms, { ...newRoom, id: roomRef.id, hotelId: hotelId, price: parseFloat(newRoom.price) }]);
        }


        setNewRoom(initialRoomState); // Reset form
        setSuccess(t('success.room_added', { number: newRoom.roomNumber }));
    } catch (error) {
        console.error("Error adding room:", error);
        setError(t('errors.room_add_failed'));
    } finally {
        setLoading(false);
    }
};
// 2. ROOM DELETION HANDLER (Can keep useCallback as it uses props/simple state)
const handleDeleteRoom = useCallback(async (roomId, roomNumber) => {
    // In a real app, this would delete a room from the 'rooms' collection
    console.log("Simulating room deletion:", roomId, roomNumber);
    setSuccess(t('success.room_deleted_sim', { number: roomNumber }));
}, [setSuccess]); // Added setSuccess to dependencies for completeness

// --- END REPLACEMENT BLOCK ---
    
    // ⭐ FIX: Add basic updateGuestStatus handler for DND status updates
    const updateGuestStatus = useCallback(async (guestId, newStatus) => {
        setLoading(true); setError(''); setSuccess('');
        try {
            await updateDoc(doc(db, 'guests', guestId), { status: newStatus });
            setSuccess(t('success.guest_status_updated', { status: t(`status.${newStatus.toLowerCase()}`) }));
        } catch (error) {
            console.error("Failed to update guest status:", error);
            setError(t('errors.guest_status_failed'));
        } finally {
            setLoading(false);
        }
    }, []);
    
    // --------------------------------------------------------------------------------
    // Data Filtering and Preparation
    // --------------------------------------------------------------------------------
    
    // Guests currently checked-in (excluding DND/checkout requested for Active Guests section)
    const activeGuests = guests.filter(g => 
        g.status === 'CHECKED_IN' && !g.checkOutRequest
    );
    // Guests who explicitly requested checkout from the app
    
    // Rooms with DND status
    const doNotDisturbRooms = rooms.filter(r => 
        r.status === 'DO_NOT_DISTURB'
    );
    


    // ⭐ FIX: Derived state for chat needed for props
    const guestDataForChat = guests.reduce((acc, guest) => {
        acc[guest.id] = { fullName: guest.fullName, roomNumber: guest.roomNumber };
        return acc;
    }, {});
    const selectedChat = activeChats.find(chat => chat.id === selectedChatId);


    // --------------------------------------------------------------------------------
    // Conditional Rendering
    // --------------------------------------------------------------------------------
    // Re-implementing renderDashboard using useCallback for performance 
// and correct dependency tracking.
const handleNavigate = useCallback((key) => {
    console.log(`Navigating to: ${key}`); // <-- ADD THIS LOG TO VERIFY IT'S RUNNING
    setActiveDashboard(key);
    }, []);
const renderDashboard = useCallback(() => {
    switch (activeDashboard) {
            case "reception":
                return (
                    <DashboardAccessGuard dashboardKey="reception"
                    key="reception">
                        {/* TEMPORARY: Check if the Guard is the only thing showing */}
            <div style={{ padding: '50px', backgroundColor: 'lightyellow' }}>
                <p>{t('dashboard.reception_active')}</p>
            </div>
                <ReceptionDashboard 
                    hotelName={hotelName} 
                    pendingRegistrationCount={pendingRegistrationCount} 
                    handleAcceptPreBooking={handleAcceptPreBooking} 
                    handleRejectPreBooking={handleRejectPreBooking} 
                    pendingGuestRequests={pendingGuestRequests} 
                    handleResolveGuestRequest={handleResolveGuestRequest} 
                    stayModificationRequests={stayModificationRequests} 
                    handleResolveStayRequest={handleResolveStayRequest} 
                    handleMarkReviewAsRead={handleMarkReviewAsRead} 
                    checkoutRequests={checkoutRequests} 
                    handleFinalizeCheckout={handleFinalizeCheckout} 
                    openBillGuestId={openBillGuestId}
                    toggleBillForm={toggleBillForm}
                    currentBillDetails={currentBillDetails}
                    handleBillChange={handleBillChange}
                    handleAddItemToBill={handleAddItemToBill}
                    handleRemoveBillItem={handleRemoveBillItem}
                    activeGuests={activeGuests} 
                    handleNotifyGuest={handleNotifyGuest} 
                    doNotDisturbRooms={doNotDisturbRooms} 
                    updateGuestStatus={updateGuestStatus} 
                    rooms={rooms} 
                    newRoom={newRoom} 
                    handleRoomChange={handleRoomChange} 
                    handleAddRoom={handleAddRoom} 
                    handleDeleteRoom={handleDeleteRoom} 
                    updateRoomStatus={updateRoomStatus} 
                    hotelSettings={hotelSettings} 
                    setHotelSettings={setHotelSettings} 
                    handleSaveSettings={handleSaveSettings} 
                    hotelId={hotelId} 
                    selectedChatId={selectedChatId} 
                    activeChats={activeChats} 
                    handleSelectChat={handleSelectChat} 
                    handleCloseChat={handleCloseChat} 
                    handleDeleteChat={handleDeleteChat} 
                    unreadChatCount={unreadChatCount} 
                    isMaximized={isMaximized} 
                    handleMaximizeToggle={handleMaximizeToggle} 
                    guestDataForChat={guestDataForChat} 
                    selectedChat={selectedChat} 
                    guestReviews={guestReviews}
                    loading={loading}
                    error={error}
                    success={success}
                />
                </DashboardAccessGuard>
            );
        
        case 'housekeeping':
            
            return (
                <DashboardAccessGuard 
                    dashboardKey="housekeeping" 
                    key="housekeeping"
                    
                >
                <HousekeepingDashboard
                    hotelId={hotelId}
                    doNotDisturbRooms={doNotDisturbRooms}
                    rooms={rooms}
                    // This filter is important to only pass active guests for context
                    guests={guests.filter(g => g.status === 'CHECKED_IN' || g.status === 'DO_NOT_DISTURB')} 
                    lostItems={lostItems}
                    handleRegisterLostItem={handleRegisterLostItem}
                    newLostItem={newLostItem}
                    setNewLostItem={setNewLostItem}
                    handleUpdateLostItemStatus={handleUpdateLostItemStatus}
                    updateRoomStatus={updateRoomStatus}
                    handleNotifyGuest={handleNotifyGuest}
                    loading={loading}
                />
                </DashboardAccessGuard>
            );
            
            // 3. CODE CHECK
           
            
        case 'inventory':
           
            return (
                <DashboardAccessGuard 
                    dashboardKey="inventory"
                    key="inventory" 
                    
                >
                <InventoryDashboard
                    hotelId={hotelId}
                    inventoryItems={inventoryItems}
                    newInventoryItem={newInventoryItem}
                    setNewInventoryItem={setNewInventoryItem}
                    handleAddNewInventoryItem={handleAddNewInventoryItem}
                    handleUpdateInventory={handleUpdateInventory}
                    loading={loading}
                />
                </DashboardAccessGuard>
            );
            
        case 'settings':
            return (
                <SettingsDashboard
                    hotelSettings={hotelSettings}
                    setHotelSettings={setHotelSettings}
                    handleSaveSettings={handleSaveSettings}
                    loading={loading}
                />
            );
            
        case 'admin':
            return (
                <div style={{ padding: '20px' }}>
                    <h2>Admin Dashboard (Under Construction)</h2>
                    <p>This panel will contain staff management, logs, and financial reports.</p>
                </div>
            );
        case 'welcome':   
        default:
            return (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h2>{t('dashboard.welcome_title')}</h2>
                    <p>{t('dashboard.welcome_msg', { name: user?.name || t('common.staff') })}</p>
                </div>
            );
    }
}, [
    activeDashboard, user, hotelName, loading, error, success, hotelId, 
    // Add ALL state variables and handler functions used inside the switch here 
    // (keeping the dependency array minimal but correct)
    pendingRegistrationCount, handleAcceptPreBooking, handleRejectPreBooking, 
    pendingGuestRequests, handleResolveGuestRequest, stayModificationRequests, 
    handleResolveStayRequest, handleMarkReviewAsRead, checkoutRequests, 
    handleFinalizeCheckout, activeGuests, handleNotifyGuest, doNotDisturbRooms,
    updateGuestStatus, rooms, newRoom, handleRoomChange, handleAddRoom, 
    handleDeleteRoom, updateRoomStatus, hotelSettings, setHotelSettings, 
    handleSaveSettings, selectedChatId, activeChats, handleSelectChat, 
    handleCloseChat, handleDeleteChat, unreadChatCount, isMaximized, 
    handleMaximizeToggle, guestDataForChat, selectedChat, guestReviews,
    guests, lostItems, handleRegisterLostItem, newLostItem, setNewLostItem,
    handleUpdateLostItemStatus, inventoryItems, newInventoryItem, setNewInventoryItem,
    handleAddNewInventoryItem, handleUpdateInventory
]);// <-- This curly brace closes the renderDashboard function.
// Ensure there are NO additional braces or statements immediately after this closing brace (}) 
// until the main component's return statement.


    if (authLoading || loading) {
        return <div style={{ padding: '40px', textAlign: 'center' }}>{t('common.loading_data')}</div>;
    }
    if (!hotelId) {
        return <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{t('errors.no_hotel_id')}
        {t('errors.check_permissions')}</div>;
    }

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: 'var(--color-bg-light)' }}>
            <Sidebar activeDashboard={activeDashboard} onNavigate={handleNavigate} />
            <Sidebar 
                activeDashboard={activeDashboard} 
                handleNavigate={handleNavigate} 
                unreadChatCount={unreadChatCount} 
          
            />
            {/* The margin-left offset is critical for fixed sidebar */}
            <div style={{ flexGrow: 1, padding: '20px', marginLeft: '250px', maxWidth: 'calc(100% - 250px)', boxSizing: 'border-box'/* Offset for fixed sidebar */ }}>
                <h1 style={{ display: 'flex', alignItems: 'center', color: 'var(--color-primary)' }}>
                    <span style={{ 
                        fontSize: '1.5em', fontWeight: 'bold' }}>É</span>picurys
                    <span style={{ color: '#aaa', marginLeft: '10px', fontWeight: 'normal' }}>| {hotelName} Dashboard</span>
                </h1>
                {error && <div style={{ color: 'white', backgroundColor: 'var(--color-error)', padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>Error: {error}</div>}
                {success && 
                    <div style={{ color: 'white', backgroundColor: 'var(--color-success)', padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>Success: {success}</div>}
                
                {/* Dashboard Metrics (Only display in Reception View) */}
                {activeDashboard === 'reception' && (
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 
                        'wrap', marginBottom: '20px',overflowX: 'auto', paddingBottom: '10px' }}>
                        <DashboardMetric title="Total Guests" value={guests.length} color="#3498db" />
                        <DashboardMetric title="Rooms Available" value={rooms.filter(r => r.status === 'available').length} color="var(--color-success)" />
                        <DashboardMetric title="Rooms Occupied" value={rooms.filter(r => r.status === 'occupied').length} 
                        color="var(--color-primary)" />
                        <DashboardMetric title="Needs Cleaning" value={rooms.filter(r => r.status === 'Needs Cleaning').length} color="var(--color-error)" />
                        <DashboardMetric title="Pending Check-ins" value={pendingRegistrationCount} color="orange" />
                        <DashboardMetric title="Unread Chats" value={unreadChatCount} color="#ff4500" />
       
                    </div>
                )}


                {/* Main Content Area based on Active Tab */}
                {renderDashboard()}

                {/* The Chat component is still rendered conditionally as a floating window */}
   
                {selectedChatId && (
                    <Chat
                        selectedChat={activeChats.find(chat => chat.id === selectedChatId)}
                        handleCloseChat={handleCloseChat}
             
                        isMaximized={isMaximized}
                        handleMaximizeToggle={handleMaximizeToggle}
                        userRole="staff"
                        hotelId={hotelId}
                 
                        staffUid={user.uid}
                        handleDeleteChat={handleDeleteChat}
                    />
                )}
            </div>
        </div>
    );

}